<template>
  <div>
    <div class="inline-flex rounded-md shadow-sm" role="group">
      <button
        v-for="(button, index) in buttonsRef"
        :key="index"
        type="button"
        :class="[
          'px-4 py-2 text-sm font-medium border focus:z-10 dark:border-gray-700',
          {
            'bg-white text-gray-900 hover:bg-gray-100 hover:text-black dark:bg-gray-800 dark:text-white dark:hover:text-black dark:hover:bg-gray-700 dark:hover:text-white dark:focus:text-white':
              !button.isGrey && !button.disabled && !button.selected,
            'bg-gray-100 text-black hover:bg-gray-200 hover:text-black  dark:bg-gray-700 dark:text-black dark:hover:bg-gray-600 dark:hover:text-black':
              button.isGrey && !button.disabled && !button.selected,
            'bg-gray-200 text-gray-500 cursor-not-allowed opacity-50 dark:bg-gray-700 dark:text-gray-500 border-t border-b border-gray-300':
              button.disabled,
            'bg-blue-700 text-white': button.selected && selectedHighlight,
            'rounded-s-lg border-gray-200': index === 0,
            'rounded-e-lg border-gray-200': index === buttons.length - 1,
            'border-t border-b border-gray-200':
              index > 0 && index < buttons.length - 1 && !button.disabled,
            'border-l-0 border-r-0': index > 0 && index < buttons.length - 1,
            'px-3': button.isIconOnly
          }
        ]"
        :disabled="button.disabled"
        @click="selectButton(index)"
      >
        <div class="flex items-center justify-center">
          <svg
            v-if="
              button.icon &&
              button.iconPosition === 'before' &&
              !button.isIconOnly
            "
            class="w-3 h-3 me-2"
            aria-hidden="true"
            v-html="button.icon"
          />
          <span v-if="!button.isIconOnly">{{ button.label }}</span>
          <svg
            v-if="
              button.icon &&
              button.iconPosition === 'after' &&
              !button.isIconOnly
            "
            class="w-3 h-3 ms-2"
            aria-hidden="true"
            v-html="button.icon"
          />
          <!-- <svg
            v-if="button.isIconOnly && button.icon"
            class="w-5 h-5"
            aria-hidden="true"
            v-html="button.icon"
          /> -->
          <i v-if="button.isIconOnly && button.icon" :class="button.icon" />
        </div>
      </button>

      <!-- <div
        v-for="(button, index) in buttonsRef"
        :id="'tooltip-' + index"
        :key="'tooltip-' + index"
        role="tooltip"
        class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
        data-popper-placement="left-start"
        data-popper-strategy="fixed"
        :data-popper-modifiers="tooltipModifiers"
        style="left: 0; transform: translateX(0); position: absolute; top: 100%"
      >
        {{ button.tooltipText }}
        <div class="tooltip-arrow" data-popper-arrow></div>
      </div> -->
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue";

interface ButtonProps {
  label?: string;
  tooltipText?: string;
  tooltipPosition?: "top" | "left" | "right" | "bottom";
  isGrey?: boolean;
  icon?: string;
  iconPosition?: "before" | "after" | null;
  isIconOnly?: boolean;
  disabled?: boolean;
  selected?: boolean;
}

export default defineComponent({
  name: "ButtonGroup",
  props: {
    buttons: {
      type: Array as PropType<ButtonProps[]>,
      required: true,
      default: () => []
    },
    selectedHighlight: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:buttons"],
  setup(props, { emit }) {
    // Initialize the buttons with their initial state from props
    const buttonsRef = ref(props.buttons.map((button) => ({ ...button })));

    // Method to handle button selection
    const selectButton = (index: number) => {
      buttonsRef.value.forEach((button, i) => {
        button.selected = i === index;
      });
      emit("update:buttons", buttonsRef.value); // Emit an event when a button is selected
    };

    // Watch for changes in the props and update the internal state accordingly
    watch(
      () => props.buttons,
      (newButtons) => {
        buttonsRef.value = newButtons.map((button) => ({ ...button }));
      },
      { deep: true }
    );

    const tooltipModifiers = [
      {
        name: "offset",
        options: {
          offset: [0, 8]
        }
      },
      {
        name: "preventOverflow",
        options: {
          altBoundary: true,
          tetherOffset: [0, 0]
        }
      },
      {
        name: "flip",
        options: {
          fallbackPlacements: ["bottom-start"]
        }
      }
    ];

    return {
      buttonsRef,
      selectButton,
      tooltipModifiers
    };
  }
});
</script>

<style scoped>
/* No custom styling needed as Flowbite handles the tooltip styles */
</style>
